// ============================================
// Stock Cases for Clinical Calibration Module
// Curated training cases for blind diagnosis practice
// ============================================

import type { StockCase } from '../types/clinicalCalibration';

const TS = '2026-02-01T08:00:00Z';

// -------------------------------------------------------
// Case 1: Sarah M. — Fatigue + TSH 3.8
// 47F, subclinical hypothyroid presentation
// Moderate acuity
// -------------------------------------------------------
const case1_SarahM: StockCase = {
  caseId: 'stock-001',
  label: 'Sarah M. — Fatigue + TSH 3.8',
  acuity: 'moderate',
  labs: [
    {
      biomarker: 'TSH',
      value: 3.8,
      unit: 'mIU/L',
      conventionalRange: '0.45–4.5',
      functionalRange: '1.0–2.5',
      status: 'above_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Free T4',
      value: 0.9,
      unit: 'ng/dL',
      conventionalRange: '0.82–1.77',
      functionalRange: '1.0–1.5',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Free T3',
      value: 2.1,
      unit: 'pg/mL',
      conventionalRange: '2.0–4.4',
      functionalRange: '3.0–4.0',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Ferritin',
      value: 22,
      unit: 'ng/mL',
      conventionalRange: '12–150',
      functionalRange: '40–100',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin D, 25-OH',
      value: 18,
      unit: 'ng/mL',
      conventionalRange: '30–100',
      functionalRange: '50–80',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin B12',
      value: 310,
      unit: 'pg/mL',
      conventionalRange: '200–900',
      functionalRange: '500–800',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Cortisol, AM',
      value: 16.2,
      unit: 'ug/dL',
      conventionalRange: '6.2–19.4',
      functionalRange: '10–15',
      status: 'above_functional',
      trigger: false,
      timestamp: TS,
    },
    {
      biomarker: 'Estradiol',
      value: 82,
      unit: 'pg/mL',
      conventionalRange: '15–350',
      functionalRange: '50–200',
      status: 'optimal',
      trigger: false,
      timestamp: TS,
    },
  ],
  transcript: [
    {
      role: 'nurse',
      text: 'Good morning! Tell me what brought you in today.',
      tags: [],
    },
    {
      role: 'patient',
      text: "I've been exhausted for the past six months. I can barely get through the workday. My PCP ran some labs and said everything looks normal, but I know something's off.",
      tags: ['fatigue', 'PCP-normal'],
    },
    {
      role: 'nurse',
      text: 'Can you describe the fatigue a bit more? Is it constant throughout the day, or does it come and go?',
      tags: [],
    },
    {
      role: 'patient',
      text: "It's worst in the morning — I hit snooze five or six times. Then I get a little burst of energy around 10 PM when I should be winding down. Classic wired-but-tired feeling.",
      tags: ['wired-tired', 'morning-fatigue'],
    },
    {
      role: 'nurse',
      text: 'Any changes in weight, hair, or skin recently?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I've gained about 12 pounds in the last year without changing what I eat. And my hair is thinning, especially at the temples. My hairdresser actually pointed it out.",
      tags: ['weight-gain', 'hair-thinning'],
    },
    {
      role: 'nurse',
      text: 'Does anyone in your family have thyroid issues?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Yes — my mom and my sister both take thyroid medication. My mom was diagnosed around my age.",
      tags: ['family-history-thyroid'],
    },
    {
      role: 'nurse',
      text: 'Are you currently taking any medications or supplements?',
      tags: [],
    },
    {
      role: 'patient',
      text: "No medications. I take a multivitamin sometimes but I'm not consistent with it.",
      tags: ['no-medications'],
    },
    {
      role: 'nurse',
      text: 'Is there anything about cost or budget we should keep in mind when recommending a plan?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Honestly, yes. I'm a teacher and my insurance doesn't cover much. I'd rather start with affordable supplements if that's an option before jumping to prescriptions.",
      tags: ['cost-sensitive', 'prefers-supplements'],
    },
  ],
  tags: [
    { tag: 'fatigue', messageIndex: 1, category: 'symptom' },
    { tag: 'PCP-normal', messageIndex: 1, category: 'history' },
    { tag: 'wired-tired', messageIndex: 3, category: 'symptom' },
    { tag: 'morning-fatigue', messageIndex: 3, category: 'symptom' },
    { tag: 'weight-gain', messageIndex: 5, category: 'symptom' },
    { tag: 'hair-thinning', messageIndex: 5, category: 'symptom' },
    { tag: 'family-history-thyroid', messageIndex: 7, category: 'history' },
    { tag: 'no-medications', messageIndex: 9, category: 'medication' },
    { tag: 'cost-sensitive', messageIndex: 11, category: 'concern' },
    { tag: 'prefers-supplements', messageIndex: 11, category: 'concern' },
  ],
  expectedDiagnosis:
    'Functional hypothyroidism (TSH above functional range with low Free T3/T4) compounded by vitamin D deficiency and iron depletion. Cortisol mildly elevated consistent with wired-tired pattern.',
  targetDecisionPoints: [
    'Primary Dx',
    'Key Lab Read',
    'Supplement-First',
    'Cost Awareness',
  ],
  createdBy: 'system',
  version: 1,
};

// -------------------------------------------------------
// Case 2: James C. — Low-T + Lisinopril
// 52M, low testosterone with existing Rx
// Complex acuity
// -------------------------------------------------------
const case2_JamesC: StockCase = {
  caseId: 'stock-002',
  label: 'James C. — Low-T + Lisinopril',
  acuity: 'complex',
  labs: [
    {
      biomarker: 'Total Testosterone',
      value: 280,
      unit: 'ng/dL',
      conventionalRange: '264–916',
      functionalRange: '500–800',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Free Testosterone',
      value: 5.2,
      unit: 'pg/mL',
      conventionalRange: '5.0–21.0',
      functionalRange: '10–18',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'SHBG',
      value: 62,
      unit: 'nmol/L',
      conventionalRange: '10–57',
      functionalRange: '20–45',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Estradiol',
      value: 38,
      unit: 'pg/mL',
      conventionalRange: '8–35',
      functionalRange: '15–30',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'DHEA-S',
      value: 120,
      unit: 'ug/dL',
      conventionalRange: '80–560',
      functionalRange: '200–400',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Cortisol, AM',
      value: 21.3,
      unit: 'ug/dL',
      conventionalRange: '6.2–19.4',
      functionalRange: '10–15',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin D, 25-OH',
      value: 24,
      unit: 'ng/mL',
      conventionalRange: '30–100',
      functionalRange: '50–80',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin B12',
      value: 420,
      unit: 'pg/mL',
      conventionalRange: '200–900',
      functionalRange: '500–800',
      status: 'below_functional',
      trigger: false,
      timestamp: TS,
    },
  ],
  transcript: [
    {
      role: 'nurse',
      text: "Welcome in. What's going on today?",
      tags: [],
    },
    {
      role: 'patient',
      text: "I'm tired all the time and my drive is gone — both at work and, honestly, in the bedroom. My wife's been on me about it.",
      tags: ['fatigue', 'low-libido'],
    },
    {
      role: 'nurse',
      text: 'How long have you been feeling this way?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Probably the last year or so. I've also lost muscle even though I'm still going to the gym three times a week. Used to be able to bench 225, now I'm struggling with 185.",
      tags: ['muscle-loss', 'exercise-regular'],
    },
    {
      role: 'nurse',
      text: 'Are you currently taking any medications?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Yes — Lisinopril 20mg for blood pressure. My doctor put me on it about two years ago. That's the only prescription.",
      tags: ['lisinopril', 'hypertension'],
    },
    {
      role: 'nurse',
      text: 'Have you tried anything for the fatigue or low drive?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I heard about TRT on a podcast — the Huberman one, I think. I want to get on testosterone. A buddy of mine started and says he feels twenty years younger.",
      tags: ['wants-TRT', 'podcast-influenced'],
    },
    {
      role: 'nurse',
      text: 'Got it. Any other supplements or over-the-counter stuff you take?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Just a protein shake after workouts. No vitamins or supplements. I know I probably should but I never remember.",
      tags: ['no-supplements'],
    },
  ],
  tags: [
    { tag: 'fatigue', messageIndex: 1, category: 'symptom' },
    { tag: 'low-libido', messageIndex: 1, category: 'symptom' },
    { tag: 'muscle-loss', messageIndex: 3, category: 'symptom' },
    { tag: 'exercise-regular', messageIndex: 3, category: 'lifestyle' },
    { tag: 'lisinopril', messageIndex: 5, category: 'medication' },
    { tag: 'hypertension', messageIndex: 5, category: 'history' },
    { tag: 'wants-TRT', messageIndex: 7, category: 'concern' },
    { tag: 'podcast-influenced', messageIndex: 7, category: 'concern' },
    { tag: 'no-supplements', messageIndex: 9, category: 'medication' },
  ],
  expectedDiagnosis:
    'Hypogonadism with elevated SHBG binding available testosterone. Elevated cortisol driving SHBG and adrenal steal. Elevated estradiol for male patient. Vitamin D deficiency. Requires provider consult (Gate 2) before TRT consideration — must evaluate Lisinopril interactions and elevated SHBG etiology.',
  targetDecisionPoints: [
    'Escalation Path',
    'Product Selection',
    'Missed Signals',
  ],
  createdBy: 'system',
  version: 1,
};

// -------------------------------------------------------
// Case 3: Maria R. — PCOS + Metformin
// 31F, PCOS on Metformin
// Complex acuity
// -------------------------------------------------------
const case3_MariaR: StockCase = {
  caseId: 'stock-003',
  label: 'Maria R. — PCOS + Metformin',
  acuity: 'complex',
  labs: [
    {
      biomarker: 'Testosterone, Total (F)',
      value: 68,
      unit: 'ng/dL',
      conventionalRange: '8–60',
      functionalRange: '10–40',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'DHEA-S',
      value: 480,
      unit: 'ug/dL',
      conventionalRange: '65–380',
      functionalRange: '100–300',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Fasting Insulin',
      value: 22,
      unit: 'uIU/mL',
      conventionalRange: '2.6–24.9',
      functionalRange: '2–8',
      status: 'above_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'HbA1c',
      value: 5.7,
      unit: '%',
      conventionalRange: '4.0–5.6',
      functionalRange: '4.5–5.3',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'TSH',
      value: 2.8,
      unit: 'mIU/L',
      conventionalRange: '0.45–4.5',
      functionalRange: '1.0–2.5',
      status: 'above_functional',
      trigger: false,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin D, 25-OH',
      value: 19,
      unit: 'ng/mL',
      conventionalRange: '30–100',
      functionalRange: '50–80',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin B12',
      value: 198,
      unit: 'pg/mL',
      conventionalRange: '200–900',
      functionalRange: '500–800',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Ferritin',
      value: 15,
      unit: 'ng/mL',
      conventionalRange: '12–150',
      functionalRange: '40–100',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
  ],
  transcript: [
    {
      role: 'nurse',
      text: 'Hi Maria, what brings you in today?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I was diagnosed with PCOS about three years ago. My periods are still really irregular — sometimes I skip two or three months. And the acne on my jawline just won't quit.",
      tags: ['PCOS-diagnosis', 'irregular-periods', 'acne'],
    },
    {
      role: 'nurse',
      text: "I see. Are you currently taking any medications for the PCOS?",
      tags: [],
    },
    {
      role: 'patient',
      text: "Yes, Metformin 500mg twice a day. My endocrinologist put me on it about a year ago. It's helped a little with the bloating but my periods are still a mess.",
      tags: ['metformin', 'endocrinologist'],
    },
    {
      role: 'nurse',
      text: 'Where do you tend to carry extra weight?',
      tags: [],
    },
    {
      role: 'patient',
      text: "All around my belly. My arms and legs are fine but I carry everything in the middle. It's so frustrating — I eat pretty clean and still can't lose it.",
      tags: ['central-adiposity', 'clean-diet'],
    },
    {
      role: 'nurse',
      text: 'Are you thinking about having children?',
      tags: [],
    },
    {
      role: 'patient',
      text: "That's actually a big reason I'm here. My husband and I want to start trying next year and I'm worried that with the PCOS it'll be hard. I want to get my body as healthy as possible first.",
      tags: ['fertility-concern', 'planning-pregnancy'],
    },
    {
      role: 'nurse',
      text: 'Any preference on how you approach treatment — medication, supplements, lifestyle changes?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I'd really prefer a natural approach if possible. I've read about inositol and some other supplements for PCOS. I don't want to add more prescriptions if I can avoid it, especially if we're trying to conceive.",
      tags: ['prefers-natural', 'interested-inositol'],
    },
  ],
  tags: [
    { tag: 'PCOS-diagnosis', messageIndex: 1, category: 'history' },
    { tag: 'irregular-periods', messageIndex: 1, category: 'symptom' },
    { tag: 'acne', messageIndex: 1, category: 'symptom' },
    { tag: 'metformin', messageIndex: 3, category: 'medication' },
    { tag: 'endocrinologist', messageIndex: 3, category: 'history' },
    { tag: 'central-adiposity', messageIndex: 5, category: 'symptom' },
    { tag: 'clean-diet', messageIndex: 5, category: 'lifestyle' },
    { tag: 'fertility-concern', messageIndex: 7, category: 'concern' },
    { tag: 'planning-pregnancy', messageIndex: 7, category: 'concern' },
    { tag: 'prefers-natural', messageIndex: 9, category: 'concern' },
    { tag: 'interested-inositol', messageIndex: 9, category: 'concern' },
  ],
  expectedDiagnosis:
    'PCOS with insulin resistance (fasting insulin elevated, HbA1c prediabetic). B12 critically low — likely Metformin-induced depletion requiring urgent supplementation. Iron deficiency (ferritin 15). Vitamin D deficient. Elevated androgens driving acne and irregular cycles. TSH borderline — monitor.',
  targetDecisionPoints: [
    'Key Lab Read',
    'Missed Signals',
    'Product Selection',
    'Supplement-First',
  ],
  createdBy: 'system',
  version: 1,
};

// -------------------------------------------------------
// Case 4: Robert C. — Polypharmacy Fatigue
// 63M, 5+ medications, fatigue
// High acuity
// -------------------------------------------------------
const case4_RobertC: StockCase = {
  caseId: 'stock-004',
  label: 'Robert C. — Polypharmacy Fatigue',
  acuity: 'high',
  labs: [
    {
      biomarker: 'TSH',
      value: 4.2,
      unit: 'mIU/L',
      conventionalRange: '0.45–4.5',
      functionalRange: '1.0–2.5',
      status: 'above_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin B12',
      value: 210,
      unit: 'pg/mL',
      conventionalRange: '200–900',
      functionalRange: '500–800',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin D, 25-OH',
      value: 14,
      unit: 'ng/mL',
      conventionalRange: '30–100',
      functionalRange: '50–80',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Ferritin',
      value: 30,
      unit: 'ng/mL',
      conventionalRange: '12–300',
      functionalRange: '50–150',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'HbA1c',
      value: 6.4,
      unit: '%',
      conventionalRange: '4.0–5.6',
      functionalRange: '4.5–5.3',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'hs-CRP',
      value: 4.8,
      unit: 'mg/L',
      conventionalRange: '0–3.0',
      functionalRange: '0–1.0',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Homocysteine',
      value: 16,
      unit: 'umol/L',
      conventionalRange: '5–15',
      functionalRange: '5–8',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Total Testosterone',
      value: 220,
      unit: 'ng/dL',
      conventionalRange: '264–916',
      functionalRange: '500–800',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
  ],
  transcript: [
    {
      role: 'nurse',
      text: "Good afternoon. What brings you in today?",
      tags: [],
    },
    {
      role: 'patient',
      text: "My wife made the appointment. She's worried because I'm exhausted all the time and I keep forgetting things — where I put my keys, people's names at work. I figured it was just getting older.",
      tags: ['fatigue', 'brain-fog', 'wife-initiated'],
    },
    {
      role: 'nurse',
      text: 'How long has the fatigue been going on?',
      tags: [],
    },
    {
      role: 'patient',
      text: "Hard to say exactly — it's been getting worse over the past couple years. I used to golf every weekend. Now I can barely drag myself off the couch on Saturdays.",
      tags: ['progressive-fatigue', 'activity-decline'],
    },
    {
      role: 'nurse',
      text: "Let's go through your medications. Can you list everything you're taking?",
      tags: [],
    },
    {
      role: 'patient',
      text: "Sure — Metformin 1000mg for blood sugar, Lisinopril 10mg for blood pressure, Atorvastatin 40mg for cholesterol, Omeprazole 20mg for acid reflux, and Amlodipine 5mg — my cardiologist added that last year for the blood pressure since the Lisinopril alone wasn't enough.",
      tags: ['metformin', 'lisinopril', 'atorvastatin', 'omeprazole', 'amlodipine', 'polypharmacy'],
    },
    {
      role: 'nurse',
      text: "That's quite a list. Have you talked to your PCP about the fatigue?",
      tags: [],
    },
    {
      role: 'patient',
      text: "Yeah, he says it's just part of getting older. 'You're 63, Bob, what do you expect?' That's basically the answer I get every time. But my wife reads a lot of health stuff and she thinks the medications might be causing some of this.",
      tags: ['PCP-dismissive', 'wife-health-advocate', 'medication-concern'],
    },
    {
      role: 'nurse',
      text: 'Your wife might be onto something. Do you take any vitamins or supplements?',
      tags: [],
    },
    {
      role: 'patient',
      text: "No, nothing. I keep meaning to start but with five pills I'm already taking every morning, I don't want to add more.",
      tags: ['no-supplements', 'pill-fatigue'],
    },
  ],
  tags: [
    { tag: 'fatigue', messageIndex: 1, category: 'symptom' },
    { tag: 'brain-fog', messageIndex: 1, category: 'symptom' },
    { tag: 'wife-initiated', messageIndex: 1, category: 'concern' },
    { tag: 'progressive-fatigue', messageIndex: 3, category: 'symptom' },
    { tag: 'activity-decline', messageIndex: 3, category: 'symptom' },
    { tag: 'metformin', messageIndex: 5, category: 'medication' },
    { tag: 'lisinopril', messageIndex: 5, category: 'medication' },
    { tag: 'atorvastatin', messageIndex: 5, category: 'medication' },
    { tag: 'omeprazole', messageIndex: 5, category: 'medication' },
    { tag: 'amlodipine', messageIndex: 5, category: 'medication' },
    { tag: 'polypharmacy', messageIndex: 5, category: 'medication' },
    { tag: 'PCP-dismissive', messageIndex: 7, category: 'history' },
    { tag: 'wife-health-advocate', messageIndex: 7, category: 'concern' },
    { tag: 'medication-concern', messageIndex: 7, category: 'concern' },
    { tag: 'no-supplements', messageIndex: 9, category: 'medication' },
    { tag: 'pill-fatigue', messageIndex: 9, category: 'concern' },
  ],
  expectedDiagnosis:
    'Multiple drug-nutrient depletions driving fatigue and cognitive decline: Metformin depletes B12 (210, near-deficient), Omeprazole depletes B12 and magnesium (compounding B12 loss), Atorvastatin depletes CoQ10. Vitamin D severely deficient (14). Elevated homocysteine (16) confirms B12/folate insufficiency and cardiovascular risk. hs-CRP 4.8 indicates systemic inflammation. HbA1c 6.4 is prediabetic trajectory despite Metformin. Total testosterone below conventional range. Requires provider escalation for testosterone and prediabetic management.',
  targetDecisionPoints: [
    'Missed Signals',
    'Escalation Path',
    'Evidence Grade',
    'Cost Awareness',
  ],
  createdBy: 'system',
  version: 1,
};

// -------------------------------------------------------
// Case 5: Amanda F. — Subclinical Hypothyroid
// 39F, classic subclinical hypo with autoimmune markers
// Moderate acuity
// -------------------------------------------------------
const case5_AmandaF: StockCase = {
  caseId: 'stock-005',
  label: 'Amanda F. — Subclinical Hypothyroid + Hashimoto\'s',
  acuity: 'moderate',
  labs: [
    {
      biomarker: 'TSH',
      value: 5.8,
      unit: 'mIU/L',
      conventionalRange: '0.45–4.5',
      functionalRange: '1.0–2.5',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Free T4',
      value: 0.8,
      unit: 'ng/dL',
      conventionalRange: '0.82–1.77',
      functionalRange: '1.0–1.5',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Free T3',
      value: 2.0,
      unit: 'pg/mL',
      conventionalRange: '2.0–4.4',
      functionalRange: '3.0–4.0',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'TPO Antibodies',
      value: 245,
      unit: 'IU/mL',
      conventionalRange: '0–34',
      functionalRange: '0–15',
      status: 'above_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin D, 25-OH',
      value: 22,
      unit: 'ng/mL',
      conventionalRange: '30–100',
      functionalRange: '50–80',
      status: 'below_conventional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Selenium',
      value: 72,
      unit: 'ug/L',
      conventionalRange: '70–150',
      functionalRange: '100–130',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Ferritin',
      value: 18,
      unit: 'ng/mL',
      conventionalRange: '12–150',
      functionalRange: '40–100',
      status: 'below_functional',
      trigger: true,
      timestamp: TS,
    },
    {
      biomarker: 'Vitamin B12',
      value: 380,
      unit: 'pg/mL',
      conventionalRange: '200–900',
      functionalRange: '500–800',
      status: 'below_functional',
      trigger: false,
      timestamp: TS,
    },
  ],
  transcript: [
    {
      role: 'nurse',
      text: 'Hi Amanda, what can we help you with today?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I'm cold all the time — like even in the summer I need a sweater. And the constipation is awful. I go maybe twice a week and that's with trying to eat fiber and drink water.",
      tags: ['cold-intolerance', 'constipation'],
    },
    {
      role: 'nurse',
      text: 'Have you noticed any other changes recently?',
      tags: [],
    },
    {
      role: 'patient',
      text: "My face looks puffy in the mornings — especially around my eyes. And I've gained about 20 pounds in the past year. I'm eating the same as always, exercising when I can. The scale just keeps going up.",
      tags: ['facial-puffiness', 'weight-gain-20lb'],
    },
    {
      role: 'nurse',
      text: 'Any family history of thyroid conditions?',
      tags: [],
    },
    {
      role: 'patient',
      text: "My mom has Hashimoto's. She was diagnosed in her early 40s and has been on levothyroxine since. She actually told me to get my antibodies checked.",
      tags: ['family-history-hashimotos', 'mom-on-levothyroxine'],
    },
    {
      role: 'nurse',
      text: 'Are you taking any medications or supplements currently?',
      tags: [],
    },
    {
      role: 'patient',
      text: "No prescriptions. I actually ordered a T3 thyroid support supplement online a few months ago — one of those glandular ones. I took it for about six weeks and honestly didn't notice any difference so I stopped.",
      tags: ['no-prescriptions', 'tried-T3-supplement', 'supplement-ineffective'],
    },
    {
      role: 'nurse',
      text: 'How do you feel about taking prescription thyroid medication if needed?',
      tags: [],
    },
    {
      role: 'patient',
      text: "I'd really rather avoid it if possible. I know my mom is on it forever and I don't love that. But if there's no other way, I'll consider it. I just want to feel like myself again.",
      tags: ['wants-to-avoid-medication', 'open-if-necessary'],
    },
  ],
  tags: [
    { tag: 'cold-intolerance', messageIndex: 1, category: 'symptom' },
    { tag: 'constipation', messageIndex: 1, category: 'symptom' },
    { tag: 'facial-puffiness', messageIndex: 3, category: 'symptom' },
    { tag: 'weight-gain-20lb', messageIndex: 3, category: 'symptom' },
    { tag: 'family-history-hashimotos', messageIndex: 5, category: 'history' },
    { tag: 'mom-on-levothyroxine', messageIndex: 5, category: 'history' },
    { tag: 'no-prescriptions', messageIndex: 7, category: 'medication' },
    { tag: 'tried-T3-supplement', messageIndex: 7, category: 'medication' },
    { tag: 'supplement-ineffective', messageIndex: 7, category: 'history' },
    { tag: 'wants-to-avoid-medication', messageIndex: 9, category: 'concern' },
    { tag: 'open-if-necessary', messageIndex: 9, category: 'concern' },
  ],
  expectedDiagnosis:
    'Hashimoto\'s thyroiditis confirmed by TPO antibodies at 245 (7x upper limit). TSH 5.8 with low Free T4 and Free T3 indicates overt hypothyroidism progressing beyond subclinical. Requires provider consult for levothyroxine evaluation (autoimmune thyroiditis = provider-level decision). Selenium supplementation evidence-based for TPO antibody reduction. Address vitamin D deficiency, iron depletion, and suboptimal B12. Prior OTC T3 glandular supplement was inappropriate and ineffective — need targeted interventions.',
  targetDecisionPoints: [
    'Primary Dx',
    'Escalation Path',
    'Supplement-First',
  ],
  createdBy: 'system',
  version: 1,
};

// -------------------------------------------------------
// Export
// -------------------------------------------------------

export const STOCK_CASES: StockCase[] = [
  case1_SarahM,
  case2_JamesC,
  case3_MariaR,
  case4_RobertC,
  case5_AmandaF,
];

export default STOCK_CASES;
